import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-yellow.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/atlanta-private-airport-transfer";
const PAGE_TITLE = "Atlanta Private Airport Transfer | VIP Private Jet Chauffeur";
const PAGE_DESC =
  "Atlanta private airport transfer service for private jet passengers, celebrities, athletes, and executives. Discreet Mercedes-Benz Sprinter & SUV chauffeurs at PDK, FTY, RYY, LZU, CNI and ATL private aviation terminals.";

const faqs = [
  {
    q: "What is a private airport transfer in Atlanta?",
    a: "A private airport transfer is a pre-arranged, dedicated chauffeur transfer between a private aviation airport (PDK, FTY, RYY, LZU) or ATL FBO and a residence, hotel, office, or venue, performed in a luxury Mercedes-Benz Sprinter or Suburban SUV.",
  },
  {
    q: "How do you coordinate a private airport pickup?",
    a: "My chauffeurs coordinate with your flight crew, track your tail number and confirm the meeting point with your FBO. Planeside pickup is available where authorized.",
  },
  {
    q: "Can you handle multi-passenger groups arriving on a private jet?",
    a: "Yes. Mercedes-Benz Sprinter Limousines seat up to 14 passengers with luggage, making them ideal for executive teams, family travel, and entourage transportation from private terminals.",
  },
  {
    q: "Do you offer long-distance private transfers from Atlanta airports?",
    a: "Absolutely. We routinely transfer clients from Atlanta private airports to destinations across Georgia and the southeast, Athens, Augusta, Macon, Greenville, Birmingham, Chattanooga, and more.",
  },
  {
    q: "Do you provide discreet transfers for celebrities and public figures?",
    a: "Yes. Chauffeurs assigned to high-profile clients sign confidentiality agreements, vehicles are unbranded with privacy glass, and pickups are staged planeside so principals never cross a public lobby. We coordinate with security details, tour managers, and advance teams.",
  },
  {
    q: "Which private aviation terminal should I fly into for my Atlanta destination?",
    a: "PDK is the closest private field to Buckhead, Brookhaven, and Sandy Springs. FTY is fastest to Downtown, Mercedes-Benz Stadium, and the convention corridor. RYY serves Cobb, The Battery, and North Georgia. LZU is the practical field for Gwinnett, Alpharetta, and Johns Creek. Tell us the destination and we will recommend the field.",
  },
  {
    q: "Do you meet passengers planeside or inside the FBO?",
    a: "Planeside wherever the FBO authorizes ramp access, so passengers step from the airstairs into the vehicle. Where ramp access is restricted, the chauffeur meets inside the private lounge and loads at the secured private curb.",
  },
];

const routeTable = {
  title: "Private Airport Transfer Times Across Metro Atlanta",
  intro:
    "Typical chauffeured drive times between Atlanta's private aviation terminals and the destinations our clients ask for most.",
  rows: [
    { route: "PDK → Buckhead", distance: "9 miles", time: "18–30 min", notes: "Shortest private arrival into Buckhead hotels and residences." },
    { route: "PDK → Midtown", distance: "13 miles", time: "22–38 min", notes: "Theatre, arts, and corporate campus arrivals." },
    { route: "FTY → Downtown / Stadium District", distance: "10 miles", time: "18–30 min", notes: "Best field for event and convention arrivals." },
    { route: "RYY → Marietta Square / The Battery", distance: "12 miles", time: "18–28 min", notes: "Avoids I-285 and ATL congestion completely." },
    { route: "LZU → Alpharetta / Johns Creek", distance: "18 miles", time: "25–40 min", notes: "North Fulton corporate campus transfers." },
    { route: "ATL FBO → Buckhead", distance: "16 miles", time: "25–45 min", notes: "Private terminal loading, no commercial curbside." },
    { route: "Any Atlanta private field → Athens", distance: "70–80 miles", time: "1 hr 15 min – 1 hr 45 min", notes: "Game weekends and campus visits." },
  ],
  footnote:
    "Typical ranges for a chauffeured Sprinter, padded against live traffic and your actual wheels-down time.",
};

const detailSections = [
  {
    title: "Private Aviation Terminals We Transfer From",
    body: [
      "A private airport transfer starts where your aircraft parks, not at a terminal curb. We operate daily at metro Atlanta's private fields and reposition statewide when itineraries run beyond the perimeter.",
    ],
    list: [
      "DeKalb-Peachtree (PDK), Atlantic Aviation, Signature, Million Air, Epps Aviation",
      "Fulton County / Charlie Brown Field (FTY), Fulton Jet Center, Hill Aircraft",
      "Cobb County McCollum Field (RYY), Atlantic Aviation",
      "Gwinnett County Briscoe Field (LZU)",
      "Cherokee County Regional (CNI) and Falcon Field, Peachtree City (FFC)",
      "Hartsfield-Jackson (ATL), Signature private terminal",
      "Statewide on request: Savannah (SAV), Augusta (AGS/DNL), Athens (AHN), Macon (MCN), Columbus (CSG), Brunswick Golden Isles (BQK)",
    ],
  },
  {
    title: "Discretion for High-Profile Passengers",
    body: [
      "Entertainers, professional athletes, and corporate principals move through Atlanta's private fields constantly, and the priority is always the same: arrive without being seen. Unmarked vehicles, privacy glass, planeside loading, and NDA-bound chauffeurs are standard rather than upgrades.",
      "For touring parties and executive teams we run multi-vehicle convoys so principals, security, and crew stay together from the ramp to the hotel, arena, studio, or estate.",
    ],
  },
];

export const Route = createFileRoute("/atlanta-private-airport-transfer")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "Atlanta private airport transfer, private airport transfer Atlanta, private jet transfer Atlanta, celebrity airport transportation Atlanta, VIP chauffeur private aviation terminal Atlanta, executive car service Atlanta FBO, Sprinter private airport transfer Atlanta, Georgia private airport chauffeur",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Atlanta Private Airport Transfer",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Private Airport Transfer",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "Airport", name: "Fulton County Airport (FTY)" },
              { type: "Airport", name: "Cobb County International Airport (RYY)" },
              { type: "Airport", name: "Briscoe Field (LZU)" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • Private Aviation Transfers"
      h1="Atlanta Private Airport Transfer"
      intro="You have the flight arranged. Let my family handle the drive, with Suburban SUVs and Mercedes-Benz Sprinter Limousines serving Atlanta's private and executive airports."
      heroImage={heroImage}
      heroAlt="Private jet taking off at sunset from Atlanta-area private aviation airport"
      midImage={midImage}
      midAlt="Executive private jet parked on the tarmac at Atlanta FBO ready for chauffeur transfer"
      sectionOneTitle="Premium Transfers From Atlanta's Private Aviation Airports"
      sectionOneBody={[
        "Atlanta Luxury Sprinters provides Atlanta private airport transfers for clients flying into PDK, Fulton County, Cobb County International, Briscoe Field, and the FBO terminals at Hartsfield-Jackson. Every transfer is pre-coordinated, flight-tracked, and chauffeured in a Mercedes-Benz Sprinter Limousine or Suburban SUV.",
        "Whether you're an executive flying privately for a board meeting in Buckhead, an entertainer arriving for a Mercedes-Benz Stadium engagement, or a family arriving for a wedding weekend, we deliver a quiet, polished, and uncompromisingly luxurious arrival experience.",
      ]}
      bulletsTitle="Designed for Private Aviation Travelers"
      bulletsIntro="Every detail handled before your aircraft touches down."
      bullets={[
        "Pre-arrival itinerary review with your flight crew or scheduler",
        "Live flight tracking with automatic schedule adjustments",
        "Planeside or FBO-curbside meet, depending on facility",
        "Bottled water, climate control, and quiet cabin standard",
        "Mercedes-Benz Sprinter Limousines for groups of 8–14",
        "Suburban SUVs for principals and small parties",
      ]}
      coverageTitle="Coverage Across Metro Atlanta"
      coverageIntro="From private terminals to luxury hotels, corporate boardrooms, and residential estates."
      coverage={[
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Dunwoody",
        "Alpharetta",
        "Marietta",
        "Johns Creek",
        "Roswell",
      ]}
      routeTable={routeTable}
      detailSections={detailSections}
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship chauffeur service for private aviation clients.",
        },
        {
          to: "/fbo-transportation-atlanta",
          title: "FBO Transportation Atlanta",
          desc: "Direct ground transportation at every Atlanta FBO.",
        },
        {
          to: "/private-jet-airports-georgia",
          title: "Georgia Private Jet Airports",
          desc: "Every private aviation terminal in Georgia, covered.",
        },
      ]}
      ctaTitle="Text Me Your Private Transfer Plans"
      ctaBody="Send me your tail number, FBO, destination, passengers and bags. I'll check availability myself and send your flat door-to-door quote in writing."
    />
  );
}
